import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Form from '@nineeng/forms';
import { useState } from 'react';
import { Tabs } from '@repo/components';
import { EventEmitter, guardedClient as guardedRequest } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { getSettings, updateSettings } from 'src/api/admin/settings';

interface ContactPersonsSettings {
	emailDomains?: string[];
}

export const Route = createFileRoute('/_admin/system/datasources')({
	loader: async () => {
		const settings = await getSettings<{ settings: ContactPersonsSettings }>('contact-persons');
		return { settings: settings?.settings || {} };
	},
	component: DatasourcesPage,
});

const defaultSettings = {
	emailDomains: [],
};

function DatasourcesPage() {
	const { settings: initialSettings } = Route.useLoaderData();
	const queryClient = useQueryClient();

	const { data: settings } = useQuery({
		queryKey: ['datasources-settings'],
		queryFn: async () => {
			const result = await getSettings<{ settings: ContactPersonsSettings }>('contact-persons');
			return result?.settings || {};
		},
		initialData: initialSettings,
	});

	const [hasChanged, setHasChanged] = useState(false);
	const [data, setData] = useState<ContactPersonsSettings>({ ...defaultSettings, ...settings });

	useBreadcrumbLevel(0, 'System');
	useBreadcrumbLevel(1, 'Datasources');

	const onChange = (formData: any) => {
		formData.emailDomains = (formData.emailDomains || []).map((domain: any) => {
			if (typeof domain === 'string') {
				return domain.trim().toLowerCase();
			} else {
				return domain.id?.trim().toLowerCase();
			}
		});

		setData(formData);
		setHasChanged(true);
	};

	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						name: 'emailDomains',
						rowLabel: 'Email filter',
						rowDescription: 'List of email domains that are selected as contact persons',
						type: 'AutoComplete',
						multiple: true,
						options: [],
						freeSolo: true,
						handleHomeEndKeys: true,
						getOptionLabel: (option: any) => {
							if (typeof option === 'string') {
								return option;
							}
							return option?.id;
						},
						renderOption: (props: any, option: any) => {
							return <li {...props}>{option?.name}</li>;
						},
						filterOptions: (options: any[], params: any) => {
							const filtered = [...options];
							const { inputValue } = params;

							if (inputValue) {
								filtered.push({
									id: inputValue,
									inputValue,
									name: `Add "${inputValue}"`,
								});
							}

							return filtered;
						},
					},
				],
			},
		],
	};

	const saveChanges = async () => {
		await updateSettings('contact-persons', data);
		EventEmitter.emit('toast', {
			type: 'success',
			message: 'Settings updated!',
		});
		setHasChanged(false);
		queryClient.invalidateQueries({ queryKey: ['datasources-settings'] });
	};

	return (
		<div className="h-full w-full p-4">
			<div className="flex mb-3">
				<Typography variant="h4">Datasources</Typography>
				<div className="flex-grow" />
				<Button disabled={!hasChanged} variant="outlined" onClick={saveChanges}>
					Save Changes
				</Button>
			</div>
			<Paper variant="outlined">
				<Tabs value={0}>
					<Tabs.TabList>
						<Tabs.Tab value={0} label="Contact Persons" />
					</Tabs.TabList>
				</Tabs>
				<div className="p-4">
					<Form schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />
				</div>
			</Paper>
		</div>
	);
}
